import { useState } from 'react';
import { Calendar as AntdCalendar, Badge, Grid, Radio } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';

const { useBreakpoint } = Grid;
const { Button: RadioButton } = Radio;

export interface CalendarEvent {
  id: string;
  date: string; // YYYY-MM-DD format
  title?: string;
  type?: string;
  color?: string;
  count?: number;
}

type CalendarView = 'month' | 'week';

/**
 * Props for the CommonCalendar component
 * @template T - The type of events to display (must extend Record<string, any>)
 */
interface CommonCalendarProps<T extends Record<string, any> = any> {
  /** Array of events to display on the calendar */
  events?: T[];
  /** Currently selected date */
  selectedDate?: Dayjs;
  /** Callback when a date is selected */
  onSelectDate?: (date: Dayjs) => void;
  /** Callback when a date is clicked */
  onDateClick?: (date: Dayjs) => void;
  /** Color mapping for event types */
  eventTypeColors?: Record<string, string>;
  /** Whether to show event count instead of title */
  showEventCount?: boolean;
  /** CSS class name */
  className?: string;
  /** Whether to show view toggle (month/week) */
  showViewToggle?: boolean;
  /** Default view mode */
  defaultView?: CalendarView;
  /** Function to extract date from event object */
  getEventDate?: (event: T) => Dayjs | string;
  /** Function to extract title from event object */
  getEventTitle?: (event: T) => string;
  /** Function to extract type from event object */
  getEventType?: (event: T) => string;
  /** Function to extract color from event object */
  getEventColor?: (event: T) => string;
  /** Callback when an event is clicked */
  onEventClick?: (event: T, date: Dayjs) => void;
  /** Legacy events for backward compatibility */
  legacyEvents?: CalendarEvent[];
}

/**
 * A flexible calendar component that can display different types of events
 * 
 * @example
 * // For Task objects
 * <CommonCalendar
 *   events={tasks}
 *   getEventDate={(task) => dayjs(task.dueDate)}
 *   getEventTitle={(task) => task.title}
 *   getEventType={(task) => task.type}
 *   onEventClick={(task) => handleViewTask(task)}
 * />
 * 
 * @example
 * // For CalendarEvent objects
 * <CommonCalendar
 *   events={calendarEvents}
 *   getEventDate={(event) => dayjs(event.start)}
 *   getEventTitle={(event) => event.title}
 *   getEventType={(event) => event.type}
 *   onEventClick={(event) => handleViewEvent(event)}
 * />
 * 
 * @example
 * // For simple objects with automatic field detection
 * <CommonCalendar
 *   events={simpleEvents}
 *   eventTypeColors={{ meeting: 'blue', activity: 'green' }}
 * />
 */
const CommonCalendar = <T extends Record<string, any> = any>({
  events = [],
  selectedDate = dayjs(),
  onSelectDate,
  onDateClick,
  eventTypeColors = {},
  showEventCount = true,
  className,
  showViewToggle = true,
  defaultView = 'week',
  getEventDate,
  getEventTitle,
  getEventType,
  getEventColor,
  onEventClick,
  legacyEvents = [],
}: CommonCalendarProps<T>) => {
  const screens = useBreakpoint();
  const [viewMode, setViewMode] = useState<CalendarView>(defaultView);

  // Helper function to get date from event
  const extractEventDate = (event: T): Dayjs => {
    if (getEventDate) {
      const date = getEventDate(event);
      return typeof date === 'string' ? dayjs(date) : date;
    }
    
    // Try common date fields
    if (event.date) return dayjs(event.date);
    if (event.start) return dayjs(event.start);
    if (event.dueDate) return dayjs(event.dueDate);
    if (event.createdAt) return dayjs(event.createdAt);
    
    return dayjs();
  };

  // Helper function to get title from event
  const extractEventTitle = (event: T): string => {
    if (getEventTitle) return getEventTitle(event);
    
    // Try common title fields
    return event.title || event.name || event.id || 'Untitled';
  };

  // Helper function to get type from event
  const extractEventType = (event: T): string => {
    if (getEventType) return getEventType(event);
    
    // Try common type fields
    return event.type || event.category || 'default';
  };

  // Helper function to get color from event
  const extractEventColor = (event: T): string => {
    if (getEventColor) return getEventColor(event);
    
    const type = extractEventType(event);
    return eventTypeColors[type] || 'blue';
  };

  const dateCellRender = (date: Dayjs) => {
    const dateStr = date.format('YYYY-MM-DD');
    
    // Handle legacy events
    const legacyDayEvents = legacyEvents.filter(event => event.date === dateStr);
    
    // Handle new generic events
    const dayEvents = events.filter(event => {
      const eventDate = extractEventDate(event);
      return eventDate.isSame(date, 'day');
    });
    
    const allEvents = [...legacyDayEvents, ...dayEvents];
    
    if (allEvents.length === 0) {
      return null;
    }

    return (
      <ul className="events" style={{ 
        listStyle: 'none', 
        margin: 0, 
        padding: 0, 
        lineHeight: '1.2em',
        fontSize: screens.xs ? '10px' : '12px'
      }}>
        {allEvents.map((item, index) => {
          const isLegacyEvent = legacyDayEvents.includes(item as CalendarEvent);
          const eventTitle = isLegacyEvent 
            ? (item as CalendarEvent).title || `${(item as CalendarEvent).count || 1}`
            : extractEventTitle(item as T);
          const eventColor = isLegacyEvent
            ? (item as CalendarEvent).color || eventTypeColors[(item as CalendarEvent).type || 'default'] || 'blue'
            : extractEventColor(item as T);
          
          return (
            <li key={isLegacyEvent ? (item as CalendarEvent).id : `${index}-${eventTitle}`}>
              {screens.xs ? (
                <Badge 
                  color={eventColor}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    if (onEventClick && !isLegacyEvent) {
                      onEventClick(item as T, date);
                    }
                  }}
                />
              ) : (
                <Badge 
                  color={eventColor}
                  text={showEventCount ? `${isLegacyEvent ? (item as CalendarEvent).count || 1 : 1}` : eventTitle}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    if (onEventClick && !isLegacyEvent) {
                      onEventClick(item as T, date);
                    }
                  }}
                />
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  const handleSelect = (date: Dayjs) => {
    if (onSelectDate) {
      onSelectDate(date);
    }
    if (onDateClick) {
      onDateClick(date);
    }
  };

  const renderViewToggle = () => {
    if (!showViewToggle) return null;

    return (
      <div style={{ marginBottom: 16, textAlign: 'center' }}>
        <Radio.Group 
          value={viewMode} 
          onChange={(e) => setViewMode(e.target.value)}
          buttonStyle="solid"
          size="small"
        >
          <RadioButton value="month">
            <CalendarOutlined /> Month
          </RadioButton>
          <RadioButton value="week">
            <ClockCircleOutlined /> Week
          </RadioButton>
        </Radio.Group>
      </div>
    );
  };

  // Week view configuration
  const weekViewProps = viewMode === 'week' ? {
    fullscreen: false,
    headerRender: ({ value, onChange }: any) => {
      const startOfWeek = value.startOf('week');
      const endOfWeek = value.endOf('week');
      return (
        <div style={{ 
          padding: '8px 16px', 
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button 
            onClick={() => onChange(value.subtract(1, 'week'))}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}
          >
            ‹
          </button>
          <span>
            {startOfWeek.format('MMM D')} - {endOfWeek.format('MMM D, YYYY')}
          </span>
          <button 
            onClick={() => onChange(value.add(1, 'week'))}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}
          >
            ›
          </button>
        </div>
      );
    },
    dateFullCellRender: (date: Dayjs) => {
      const dateStr = date.format('YYYY-MM-DD');
      
      // Handle legacy events
      const legacyDayEvents = legacyEvents.filter(event => event.date === dateStr);
      
      // Handle new generic events
      const dayEvents = events.filter(event => {
        const eventDate = extractEventDate(event);
        return eventDate.isSame(date, 'day');
      });
      
      const allEvents = [...legacyDayEvents, ...dayEvents];
      
      const isToday = date.isSame(dayjs(), 'day');
      const isSelected = date.isSame(selectedDate, 'day');
      const isCurrentMonth = date.isSame(selectedDate, 'month');
      
      return (
        <div
          style={{
            height: '100%',
            padding: '4px',
            border: isToday ? '2px solid #1890ff' : '1px solid #f0f0f0',
            backgroundColor: isSelected ? '#e6f7ff' : (isCurrentMonth ? 'white' : '#fafafa'),
            borderRadius: '4px',
            cursor: 'pointer',
            minHeight: '80px',
            display: 'flex',
            flexDirection: 'column',
            opacity: isCurrentMonth ? 1 : 0.6
          }}
          onClick={() => handleSelect(date)}
        >
          <div style={{ 
            fontSize: '12px', 
            fontWeight: isToday ? 'bold' : 'normal',
            color: isToday ? '#1890ff' : '#666',
            marginBottom: '4px'
          }}>
            {date.format('ddd')}
          </div>
          <div style={{ 
            fontSize: '14px', 
            fontWeight: isToday ? 'bold' : 'normal',
            marginBottom: '4px'
          }}>
            {date.format('D')}
          </div>
          {allEvents.length > 0 && (
            <div style={{ flex: 1, overflow: 'hidden' }}>
              {allEvents.slice(0, 3).map((item, index) => {
                const isLegacyEvent = legacyDayEvents.includes(item as CalendarEvent);
                const eventTitle = isLegacyEvent 
                  ? (item as CalendarEvent).title || `${(item as CalendarEvent).count || 1}`
                  : extractEventTitle(item as T);
                const eventColor = isLegacyEvent
                  ? (item as CalendarEvent).color || eventTypeColors[(item as CalendarEvent).type || 'default'] || 'blue'
                  : extractEventColor(item as T);
                
                return (
                  <div
                    key={isLegacyEvent ? (item as CalendarEvent).id : `${index}-${eventTitle}`}
                    style={{
                      backgroundColor: eventColor,
                      color: 'white',
                      fontSize: '10px',
                      padding: '1px 4px',
                      margin: '1px 0',
                      borderRadius: '2px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer'
                    }}
                    title={eventTitle}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onEventClick && !isLegacyEvent) {
                        onEventClick(item as T, date);
                      }
                    }}
                  >
                    {eventTitle}
                  </div>
                );
              })}
              {allEvents.length > 3 && (
                <div style={{
                  fontSize: '10px',
                  color: '#666',
                  textAlign: 'center',
                  marginTop: '2px'
                }}>
                  +{allEvents.length - 3} more
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
  } : {};

  return (
    <div className={className}>
      {renderViewToggle()}
      <AntdCalendar
        value={selectedDate}
        onSelect={handleSelect}
        cellRender={viewMode === 'month' ? dateCellRender : undefined}
        fullscreen={viewMode === 'month' && !screens.xs}
        style={{ 
          border: '1px solid #f0f0f0',
          borderRadius: '8px',
          padding: screens.xs ? '8px' : '16px'
        }}
        {...weekViewProps}
      />
    </div>
  );
};

export default CommonCalendar; 